import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Switch,
} from "@repo/ui";
import { useEffect, useMemo, useState } from "react";

type AvailabilityItem = { tent_type: string; remaining: number };
interface AvailabilityResponse {
  availability: AvailabilityItem[];
}

async function getAvailability(checkIn: string, checkOut: string): Promise<AvailabilityResponse> {
  const params = new URLSearchParams({ check_in: checkIn, check_out: checkOut });
  const res = await fetch(`/api/availability?${params.toString()}`);
  return res.json();
}

export default function Booking() {
  const [checkIn, setCheckIn] = useState<string>("");
  const [checkOut, setCheckOut] = useState<string>("");
  const [tent, setTent] = useState<string>("King Bed Tent");
  const [qty, setQty] = useState<number>(1);
  const [availability, setAvailability] = useState<Record<string, number>>({});
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [breakfast, setBreakfast] = useState(false);
  const [payment, setPayment] = useState<"promptpay" | "linepay">("promptpay");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const price = useMemo(() => (tent === "King Bed Tent" ? 2500 : 3500), [tent]);

  useEffect(() => {
    if (checkIn && checkOut) {
      getAvailability(checkIn, checkOut).then((d) => {
        const map: Record<string, number> = {};
        (d.availability || []).forEach((a) => (map[a.tent_type] = a.remaining));
        setAvailability(map);
      });
    }
  }, [checkIn, checkOut]);

  async function submit() {
    setSubmitting(true);
    setMessage(null);
    const res = await fetch("/api/book", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        tent_type: tent,
        check_in: checkIn,
        check_out: checkOut,
        quantity: qty,
        guest_name: name,
        guest_email: email,
        guest_phone: phone,
        breakfast,
        payment_method: payment,
      }),
    });
    const data: { error?: string } = await res.json();
    if (!res.ok) setMessage(data.error || "Something went wrong");
    else setMessage("Booking placed! We'll confirm shortly.");
    setSubmitting(false);
  }

  const nights = useMemo(() => {
    if (!checkIn || !checkOut) return 0;
    const a = new Date(checkIn);
    const b = new Date(checkOut);
    return Math.max(0, Math.round((Number(b) - Number(a)) / (1000 * 60 * 60 * 24)));
  }, [checkIn, checkOut]);

  const total = nights * price * qty + (breakfast ? 200 * qty * Math.max(1, nights) : 0);

  return (
    <section className="max-w-5xl mx-auto px-4 py-12 grid md:grid-cols-5 gap-8">
      <div className="md:col-span-3 space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Your stay</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4">
            <div className="grid md:grid-cols-2 gap-3">
              <div className="grid gap-2">
                <Label>Check‑in</Label>
                <Input type="date" value={checkIn} onChange={(e) => setCheckIn(e.target.value)} />
              </div>
              <div className="grid gap-2">
                <Label>Check‑out</Label>
                <Input type="date" value={checkOut} onChange={(e) => setCheckOut(e.target.value)} />
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="grid gap-2">
                <Label>Tent type</Label>
                <Select value={tent} onValueChange={(v) => setTent(v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="King Bed Tent">King Bed Tent</SelectItem>
                    <SelectItem value="Double‑Queen Tent">Double‑Queen Tent</SelectItem>
                  </SelectContent>
                </Select>
                {checkIn && checkOut && (
                  <p className="text-xs text-muted-foreground">
                    Remaining: {availability[tent] ?? "-"}
                  </p>
                )}
              </div>
              <div className="grid gap-2">
                <Label>Quantity</Label>
                <Input
                  type="number"
                  min={1}
                  max={availability[tent] || 4}
                  value={qty}
                  onChange={(e) => setQty(Number(e.target.value))}
                />
              </div>
              <div className="grid gap-2">
                <Label>Breakfast add‑on</Label>
                <div className="h-10 px-3 rounded-md border flex items-center justify-between">
                  <span className="text-sm">+ THB 200 / person / day</span>
                  <Switch checked={breakfast} onCheckedChange={setBreakfast} />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Guest details</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3">
            <div className="grid gap-2">
              <Label>Name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Full name"
              />
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="grid gap-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                />
              </div>
              <div className="grid gap-2">
                <Label>Phone</Label>
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+66" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Payment</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-3">
              <Button
                variant={payment === "promptpay" ? "default" : "outline"}
                onClick={() => setPayment("promptpay")}
              >
                PromptPay QR
              </Button>
              <Button
                variant={payment === "linepay" ? "default" : "outline"}
                onClick={() => setPayment("linepay")}
              >
                LINE Pay
              </Button>
            </div>
            {payment === "promptpay" ? (
              <div className="p-4 border rounded-lg bg-muted/50 text-sm">
                Provide your PromptPay merchant ID to generate a live QR. For now, we show a
                placeholder for demonstration.
                <div className="mt-3 aspect-video bg-muted rounded-md" />
              </div>
            ) : (
              <div className="p-4 border rounded-lg bg-muted/50 text-sm">
                Provide your LINE Pay link to enable one‑tap checkout. Placeholder shown for now.
                <div className="mt-3 aspect-video bg-muted rounded-md" />
              </div>
            )}
            <Separator />
            <Button
              disabled={submitting || !checkIn || !checkOut || !name}
              onClick={submit}
              className="w-full"
            >
              Confirm Booking
            </Button>
            {message && <p className="text-sm text-muted-foreground">{message}</p>}
          </CardContent>
        </Card>
      </div>

      <div className="md:col-span-2">
        <Card>
          <CardHeader>
            <CardTitle>Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span>{tent}</span>
              <span>THB {price.toLocaleString()} / night</span>
            </div>
            <div className="flex justify-between text-muted-foreground">
              <span>
                {qty} tent(s) × {nights} night(s)
              </span>
              <span>THB {(price * qty * nights).toLocaleString()}</span>
            </div>
            {breakfast && (
              <div className="flex justify-between text-muted-foreground">
                <span>Breakfast add‑on</span>
                <span>THB {(200 * qty * Math.max(1, nights)).toLocaleString()}</span>
              </div>
            )}
            <Separator className="my-2" />
            <div className="flex justify-between font-medium">
              <span>Total</span>
              <span>THB {total.toLocaleString()}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Taxes included. Pay securely via PromptPay or LINE Pay.
            </p>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
